import { SUSPICION_LEVELS, ADVISORY_LABEL } from '@aytm/app/stats/page/data-centrifuge/lib/vectors'

// The Total row has no running percentage and its cell stays blank. The table draws a dash for a
// cell with no value, an empty string included, so the blank is a non-breaking space.
export const BLANK_CELL = '&nbsp;'

export const calcTableProps = ({
  filter,
  scores = [],
  scoresFiltered,
  excludedScores,
  suspicionScores,
  advisoryFiltered = [],
  dcAnalyzedCount = 0,
  notAnalyzedCount = 0
}) => {
  const cols = [
    {
      type: 'title',
      title: `Suspicion threshold among <b>${
        ('quarantined' == filter && 'Quarantined only') ||
        ('flagged' == filter && 'Flagged & not quarantined') ||
        'All flagged'
      }</b>`
    },
    {
      type: 'value',
      title: 'N'
    },
    {
      type: 'value',
      title: '%',
      format: function (value) {
        return `${value.toFixed(1)}%`
      }
    },
    {
      type: 'value',
      title: '% cumulative',
      format: function (value) {
        return 'number' == typeof value ? `${value.toFixed(1)}%` : (value ?? '')
      }
    }
  ]

  const answerVariants = [
    {
      text: 'High',
      position: 1
    },
    {
      text: 'Moderate',
      position: 2
    },
    {
      text: 'Low',
      position: 3
    },
    {
      text: 'Minor',
      position: 4
    },
    {
      text: ADVISORY_LABEL,
      position: 5
    },
    {
      text: 'No flags',
      position: 6
    },
    {
      text: '<b>Total</b>',
      position: 7
    }
  ]

  const rows = answerVariants.map((av, avi) => {
    return {
      cells: [
        {
          title: av.text
        },
        {
          value: 0,
          classNames: 6 > avi ? [] : ['bold']
        },
        {
          value: 0,
          classNames: 6 > avi ? [] : ['bold']
        },
        {
          value: 6 > avi ? 0 : BLANK_CELL
        }
      ],
      position: av.position
    }
  })

  // The response payload only carries respondents DC has something to say about, so
  // `excludedScores` (received-but-unflagged) badly under-counts the clean population and the
  // No-flags row loses most of its data. Derive it from the run's own totals instead: everyone
  // the run covered, minus everyone it flagged. Falls back to the received population when the
  // run totals are absent (no run yet, or a tier that withholds them), which reproduces the
  // previous behaviour exactly.
  const received = scores.length + excludedScores.length
  const covered = dcAnalyzedCount + notAnalyzedCount || received
  // Advisory responses are part of the covered population and are no longer clean, so they move
  // out of No-flags rather than being counted in both rows.
  const advisory = advisoryFiltered.length
  const noFlags = Math.max(0, covered - scores.length - advisory)

  // Percentages are relative to what the table actually represents: the flagged rows it shows
  // plus the Advisory and No-flags rows. With no filter and no deselected vectors that equals
  // `covered`.
  const denominator = scoresFiltered.length + advisory + noFlags

  if (0 < denominator) {
    for (const [si, sv] of SUSPICION_LEVELS.entries()) {
      const l = scoresFiltered.filter(s => sv[1] == suspicionScores[s[0]]).length

      rows[si].cells[1].value = l
      rows[si].cells[2].value = (100 * l) / denominator
      rows[si].cells[3].value = Object.values(rows).reduce((acc, cur) => acc + cur.cells[2].value, 0)

      rows[6].cells[1].value += rows[si].cells[1].value
    }

    rows[4].cells[1].value = advisory
    rows[4].cells[2].value = (100 * advisory) / denominator
    rows[4].cells[3].value = Object.values(rows).reduce((acc, cur) => acc + cur.cells[2].value, 0)

    rows[5].cells[1].value = noFlags
    rows[5].cells[2].value = (100 * noFlags) / denominator
    rows[5].cells[3].value = Object.values(rows).reduce((acc, cur) => acc + cur.cells[2].value, 0)

    rows[6].cells[1].value += rows[4].cells[1].value + rows[5].cells[1].value
    rows[6].cells[2].value = (100 * rows[6].cells[1].value) / denominator
  }

  return { cols, rows, classNames: ['data-centrifuge'] }
}
